import React, { useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Polyline, Popup } from 'react-leaflet';
import { AlertTriangleIcon, MapPinOffIcon } from 'lucide-react';
import { ActingDetailQueryResponse } from '@area-restrita/api-client/model/actingDetailQueryResponse';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import CargoTileLayer from '@/components/Map/CargoTileLayer';

import ActingRouteLayerControls from './ActingRouteLayerControls';
import ActingRouteMapBounds from './ActingRouteMapBounds';
import ActingRouteMapLegend from './ActingRouteMapLegend';
import PassengerEventMarker from './PassengerEventMarker';
import { visiblePlannedRoute } from './visiblePlannedRoute';

interface IProps {
  acting: ActingDetailQueryResponse;
}

type MapPosition = [number, number];

const toMapPositions = (coordinates: number[][]): MapPosition[] =>
  coordinates.map(([lng, lat]) => [lat, lng]);

const ActingRouteMap: React.FC<IProps> = ({ acting }) => {
  const [showPlannedRoute, setShowPlannedRoute] = useState(true);
  const [showAnalyzedTrace, setShowAnalyzedTrace] = useState(true);
  const plannedRoute = useMemo(
    () =>
      acting.routeComparison.plannedRoute
        ? toMapPositions(acting.routeComparison.plannedRoute.coordinates)
        : [],
    [acting.routeComparison.plannedRoute],
  );
  const analyzedTraceSegments = useMemo(
    () =>
      acting.routeComparison.analyzedTraceSegments.map(({ coordinates }) =>
        toMapPositions(coordinates),
      ),
    [acting.routeComparison.analyzedTraceSegments],
  );
  const plannedRouteSegments = useMemo(
    () =>
      showAnalyzedTrace
        ? visiblePlannedRoute(plannedRoute, analyzedTraceSegments)
        : [plannedRoute],
    [plannedRoute, analyzedTraceSegments, showAnalyzedTrace],
  );
  const firstAnalyzedPosition = analyzedTraceSegments.at(0)?.at(0);
  const lastAnalyzedPosition = analyzedTraceSegments.at(-1)?.at(-1);
  const initialCenter = plannedRoute.at(0) ?? firstAnalyzedPosition;
  const reliablePassengerEvents = acting.passengerEvents.filter(
    ({ locationStatus }) => locationStatus === 'used_in_analyzed_trace',
  );
  const excludedPassengerEventCount =
    acting.passengerEvents.length - reliablePassengerEvents.length;
  const hasPlannedRoute = plannedRoute.length > 0;
  const hasAnalyzedTrace = analyzedTraceSegments.length > 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Percurso da execução</CardTitle>
            <CardDescription>
              Comparação entre a rota planejada e o traçado analisado
            </CardDescription>
          </div>
          <ActingRouteMapLegend isActingOpen={acting.status === 'started'} />
        </div>
        <ActingRouteLayerControls
          showPlannedRoute={showPlannedRoute}
          showAnalyzedTrace={showAnalyzedTrace}
          onPlannedRouteChange={setShowPlannedRoute}
          onAnalyzedTraceChange={setShowAnalyzedTrace}
        />
      </CardHeader>
      <CardContent className="space-y-3 p-0">
        <div className="space-y-3 px-6">
          {acting.routeComparison.plannedRouteSource ===
            'current_busline_fallback' && (
            <Alert>
              <AlertTriangleIcon />
              <AlertTitle>
                Comparação baseada na configuração atual da rota
              </AlertTitle>
            </Alert>
          )}
          {hasPlannedRoute && !hasAnalyzedTrace && (
            <Alert>
              <AlertTriangleIcon />
              <AlertTitle>Traçado analisado indisponível</AlertTitle>
              <AlertDescription>
                Não foi possível formar um traçado confiável com as posições
                registradas.
              </AlertDescription>
            </Alert>
          )}
          {!hasPlannedRoute && hasAnalyzedTrace && (
            <Alert>
              <AlertTriangleIcon />
              <AlertTitle>Comparação indisponível</AlertTitle>
              <AlertDescription>
                A rota planejada não está disponível para esta execução.
              </AlertDescription>
            </Alert>
          )}
          {excludedPassengerEventCount > 0 && (
            <Alert>
              <MapPinOffIcon />
              <AlertTitle>
                {excludedPassengerEventCount} eventos de passageiros sem
                localização confiável
              </AlertTitle>
            </Alert>
          )}
        </div>
        {!initialCenter ? (
          <div className="flex min-h-[480px] items-center justify-center p-6">
            <Alert className="max-w-md">
              <MapPinOffIcon />
              <AlertTitle>Nenhum percurso disponível</AlertTitle>
              <AlertDescription>
                Não há rota planejada nem traçado analisado para exibir.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <MapContainer
            center={initialCenter}
            zoom={17}
            scrollWheelZoom
            className="h-[480px] lg:h-[620px]"
            style={{ zIndex: 0 }}
          >
            <CargoTileLayer />
            {showPlannedRoute &&
              hasPlannedRoute &&
              plannedRouteSegments.map((positions, index) => (
                <Polyline
                  key={index}
                  positions={positions}
                  pathOptions={{
                    color: '#2563eb',
                    dashArray: '10 8',
                    opacity: 0.9,
                    weight: 4,
                  }}
                />
              ))}
            {showAnalyzedTrace &&
              analyzedTraceSegments.map((positions, index) => (
                <Polyline
                  key={index}
                  positions={positions}
                  pathOptions={{
                    color: 'var(--primary)',
                    opacity: 0.9,
                    weight: 5,
                  }}
                />
              ))}
            {showAnalyzedTrace && firstAnalyzedPosition && (
              <CircleMarker
                center={firstAnalyzedPosition}
                radius={8}
                pathOptions={{
                  color: 'white',
                  fillColor: '#111827',
                  fillOpacity: 1,
                  weight: 3,
                }}
              >
                <Popup>Início do traçado analisado</Popup>
              </CircleMarker>
            )}
            {showAnalyzedTrace &&
              lastAnalyzedPosition &&
              lastAnalyzedPosition !== firstAnalyzedPosition && (
                <CircleMarker
                  center={lastAnalyzedPosition}
                  radius={8}
                  pathOptions={{
                    color: 'white',
                    fillColor: 'var(--primary)',
                    fillOpacity: 1,
                    weight: 3,
                  }}
                >
                  <Popup>
                    {acting.status === 'started'
                      ? 'Posição atual analisada'
                      : 'Fim do traçado analisado'}
                  </Popup>
                </CircleMarker>
              )}
            {reliablePassengerEvents.map((event) => (
              <PassengerEventMarker key={event.id} event={event} />
            ))}
            <ActingRouteMapBounds
              actingId={acting.id}
              plannedRoute={plannedRoute}
              analyzedTraceSegments={analyzedTraceSegments}
            />
          </MapContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default ActingRouteMap;
